import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ActionMenu, Button, IconButton, isApple, ModalDialog, modifierKey, withModifier, } from '@kanos/ui';
import { BookOpen, CircleHelp, Compass, Keyboard, LifeBuoy, Megaphone, NotebookText, } from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';
import { supportAddress } from './app-footer.js';
/**
 * Every shortcut of the apps, in one list (WCAG 2.1.4: none is a single character key - each
 * holds a modifier or works only where the focus is). The overview shows exactly these.
 */
function shortcuts() {
    const mod = modifierKey();
    return [
        {
            title: 'Überall',
            items: [
                { keys: [mod, 'K'], does: 'Suchen und zu einer Seite springen' },
                { keys: [mod, '/'], does: 'Diese Übersicht öffnen' },
            ],
        },
        {
            title: 'Bewegen',
            items: [
                { keys: ['Tab'], does: 'Zum nächsten Bedienelement – das erste führt zum Inhalt' },
                { keys: ['Umschalt', 'Tab'], does: 'Zum vorigen Bedienelement' },
            ],
        },
        {
            title: 'In Menüs, Listen und Dialogen',
            items: [
                { keys: ['↑', '↓'], either: true, does: 'Eintrag wählen' },
                { keys: ['Enter'], does: 'Ausführen oder öffnen' },
                { keys: ['Esc'], does: 'Schließen – der Fokus kehrt zurück' },
            ],
        },
    ];
}
function Keys({ keys, either }) {
    return (_jsx("span", { className: "flex shrink-0 items-center gap-1", children: keys.map((key, i) => (_jsxs(Fragment, { children: [i > 0 &&
                    (either ? (_jsx("span", { className: "text-sm text-muted", children: "oder" })) : (_jsx("span", { className: "sr-only", children: " plus " }))), _jsx("kbd", { className: "min-w-7 rounded-xs bg-fill px-1.5 py-0.5 text-center font-sans text-sm ring-1 ring-hairline ring-inset", children: key })] }, key))) }));
}
/** The overview of the shortcuts, as a dialog. */
function ShortcutsDialog({ isOpen, onClose }) {
    return (_jsx(ModalDialog, { title: "Tastenk\u00FCrzel", description: "Kanos l\u00E4sst sich ganz mit der Tastatur bedienen.", isOpen: isOpen, onOpenChange: (open) => {
            if (!open)
                onClose();
        }, children: (close) => (_jsxs(_Fragment, { children: [shortcuts().map((group) => (_jsxs("section", { className: "grid gap-2", children: [_jsx("h3", { className: "text-sm font-semibold text-muted", children: group.title }), _jsx("dl", { className: "divide-y divide-hairline border-y border-hairline", children: group.items.map((item) => (_jsxs("div", { className: "flex items-center justify-between gap-4 py-2.5", children: [_jsx("dt", { className: "text-[0.9375rem]", children: item.does }), _jsx("dd", { children: _jsx(Keys, { keys: item.keys, either: item.either }) })] }, item.does))) })] }, group.title))), _jsx(Button, { variant: "secondary", className: "justify-self-end", onPress: close, children: "Schlie\u00DFen" })] })) }));
}
/**
 * Help at the right of the bar (after Corly's help menu): help on the page one is on, all
 * articles, the tour through the app, the shortcuts - which also open with ⌘/ or Strg / -
 * and the way to the support where there is one. `page` is the article on this page, if any;
 * `news` what is new in Kanos, with a dot on the button while some of it is unread.
 */
export function HelpMenu({ page, onArticles, onTour, news, }) {
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const support = supportAddress();
    useEffect(() => {
        const onKey = (event) => {
            if (withModifier(event) && event.key === '/') {
                event.preventDefault();
                setShortcutsOpen((now) => !now);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);
    const articles = [
        ...(page
            ? [
                {
                    id: 'page',
                    label: 'Hilfe zu dieser Seite',
                    description: page.title,
                    icon: BookOpen,
                    onAction: page.open,
                },
            ]
            : []),
        { id: 'all', label: 'Alle Hilfeartikel', icon: NotebookText, onAction: onArticles },
        ...(onTour ? [{ id: 'tour', label: 'Rundgang starten', icon: Compass, onAction: onTour }] : []),
        ...(news
            ? [
                {
                    id: 'news',
                    label: 'Neuigkeiten',
                    description: news.unread > 0 ? `${String(news.unread)} neu` : 'Was sich in Kanos ändert',
                    icon: Megaphone,
                    onAction: news.open,
                },
            ]
            : []),
    ];
    const unread = news?.unread ?? 0;
    return (_jsxs(_Fragment, { children: [_jsx(ActionMenu, { label: "Hilfe", trigger: _jsxs(IconButton, { "aria-label": unread > 0 ? `Hilfe, Neuigkeiten: ${String(unread)} ungelesen` : 'Hilfe', className: "relative", children: [_jsx(CircleHelp, { "aria-hidden": true, className: "size-5" }), unread > 0 && (_jsx("span", { "aria-hidden": true, className: "absolute top-1.5 right-1.5 size-2.5 rounded-full bg-accent-text ring-2 ring-surface" }))] }), groups: [
                    { id: 'articles', items: articles },
                    {
                        id: 'keys',
                        items: [
                            {
                                id: 'shortcuts',
                                label: 'Tastenkürzel',
                                description: `${modifierKey()}${isApple() ? '' : ' '}/`,
                                icon: Keyboard,
                                onAction: () => setShortcutsOpen(true),
                            },
                        ],
                    },
                    {
                        id: 'support',
                        items: support
                            ? [
                                {
                                    id: 'support',
                                    label: 'Hilfe & Kontakt',
                                    description: support,
                                    icon: LifeBuoy,
                                    onAction: () => window.location.assign(`mailto:${support}`),
                                },
                            ]
                            : [],
                    },
                ] }), _jsx(ShortcutsDialog, { isOpen: shortcutsOpen, onClose: () => setShortcutsOpen(false) })] }));
}
//# sourceMappingURL=help-menu.js.map